import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faImage, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./ImagePicker.module.css";
import axiosInstance from "../../services/axiosInstance";
import { IMAGE_PROCESSING_ERROR, normalizeImageFile } from "../../utils/normalizeImageFile";

function ImagePicker({ onSelect, maxSize = 10 * 1024 * 1024, label = "Selecionar foto", disabled = false, capture = "environment", onError }) {
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mounted = useRef(true);
  const busy = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const cameraRef = useRef(null); const galleryRef = useRef(null); const titleId = useId();
  const report = (diagnostics) => { if (diagnostics) axiosInstance.post("posts/image-processing-diagnostics", diagnostics, { timeout: 5000 }).catch(() => {}); };
  const pick = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file || busy.current) return;
    busy.current = true;
    setOpen(false); setProcessing(true);
    try {
      const normalized = await normalizeImageFile(file, { maxSize });
      report(normalized.diagnostics);
      if (!mounted.current) { URL.revokeObjectURL(normalized.previewUrl); return; }
      if (normalized.file.size > maxSize) { URL.revokeObjectURL(normalized.previewUrl); onError?.(`A foto processada deve ter até ${Math.round(maxSize / 1024 / 1024)} MB.`); return; }
      onSelect(normalized.file, normalized);
    } catch (error) {
      report(error.diagnostics);
      console.warn("Falha ao processar foto no dispositivo", { type: file.type, size: file.size, cause: error.cause?.message || error.message });
      if (mounted.current) onError?.(error.message || IMAGE_PROCESSING_ERROR);
    }
    finally { input.value = ""; busy.current = false; if (mounted.current) setProcessing(false); }
  };
  const launch = (ref) => { setOpen(false); ref.current?.click(); };
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", close);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", close); document.body.style.overflow = previous; };
  }, [open]);
  return <>
    <button type="button" className={styles.trigger} onClick={() => setOpen(true)} disabled={disabled || processing}>{processing ? "Processando foto..." : label}</button>
    <input className={styles.hidden} ref={cameraRef} type="file" accept="image/*" capture={capture} onChange={pick} />
    <input className={styles.hidden} ref={galleryRef} type="file" accept="image/*" onChange={pick} />
    {open && createPortal(<div className={styles.backdrop} role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
      <section className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header><h2 id={titleId}>Adicionar foto</h2><button type="button" onClick={() => setOpen(false)} aria-label="Fechar"><FontAwesomeIcon icon={faXmark} /></button></header>
        <button type="button" onClick={() => launch(cameraRef)}><FontAwesomeIcon icon={faCamera} /><span><strong>Tirar foto</strong><small>Usar a câmera do dispositivo</small></span></button>
        <button type="button" onClick={() => launch(galleryRef)}><FontAwesomeIcon icon={faImage} /><span><strong>Escolher da galeria</strong><small>Selecionar uma imagem existente</small></span></button>
        <button type="button" className={styles.cancel} onClick={() => setOpen(false)}>Cancelar</button>
      </section>
    </div>, document.body)}
  </>;
}
export default ImagePicker;
